import { useAccount, useMsal } from "@azure/msal-react";
import axios from "axios";
import { FormikHelpers } from "formik";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import styled from "styled-components";
import { loginUser } from "../../../../app/App";
import { RoleStatus, UserPermissions } from "../../../../enums";

import { Api } from "../../../../services/api";
import { Role, RoleMembers } from "../../../../services/api/types/Roles";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { setMembers } from "../../../../store/slices/MemberListSlice";
import { ColumnContainer } from "../../Admin.styles";
import { RoleForm } from "../RolesForm";
import { RoleFormHeader, RoleFormType } from "../RolesForm/rolesFormSchema";
import { Tabs } from "../Tabs";

const Container = styled(ColumnContainer)`
  gap: 72px;
`;

export const EditRoleForm: React.FC<{}> = () => {
  const defaultRole = {
    id: "",
    name: "",
    description: "",
    status: "",
    permissions: [],
    members: [],
  };

  const dispatch = useAppDispatch();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const history = useHistory();
  const user = useAppSelector(state => state.auth.user);
  const [isLoading, setIsLoading] = useState(true);
  const [rolePermissions, setRolePermissions] = useState<any>([]);
  const [role, setRole] = useState<Role>(defaultRole);
  const [formRole, setFormRole] = useState<RoleFormType>();
  const { roleId } = useParams<{ roleId: string }>();
  const selectedRows = useRef([]);

  useEffect(() => {
    fetchRole();
  }, [roleId]);

  const fetchRole = async () => {
    try {
      const response = await Api.roles.getRole(roleId);
      const roleStatus = (response.data.status =
        RoleStatus[response.data.status as number]);
      const data: RoleFormType = {
        status: roleStatus,
        name: response.data.name,
        description: response.data.description,
      };
      setRole(response.data);
      setFormRole(data);
      setRolePermissions(response.data.permissions);
      setIsLoading(false);
    } catch (e) {
      console.error("Fetch Role Error:", e);
    }
  };

  const submitValues = (
    values: RoleFormType,
    { setErrors }: FormikHelpers<RoleFormType>
  ) => {
    const data: Role = {
      id: roleId,
      status:
        values.status === "Active" ? RoleStatus.Active : RoleStatus.Inactive,
      name: values.name || "",
      description: values.description || "",
      permissions: rolePermissions,
      members: role.members,
    };
    Api.roles
      .updateRole(roleId, data)
      .then(() => {
        if (role.members.findIndex(member => member.id === user.id) !== -1) {
          loginUser(dispatch, instance, account);
          Api.members
            .getMembers()
            .then(res => {
              dispatch(setMembers(res.data));
            })
            .catch(() => {
              dispatch(setMembers([]));
            });
        }
        const currentLocation = history.location;
        currentLocation.pathname = "/admin/roles";
        history.push(currentLocation);
      })
      .catch((error: Error) => {
        if (
          axios.isAxiosError(error) &&
          error.response &&
          error.response.data.detail ===
            'duplicate key value violates unique constraint "roles_name_key"'
        ) {
          setErrors({ name: "Name already used" });
        } else {
          console.error("Create Office", error);
        }
      });
  };

  let handleAddTeamMember = undefined;
  if (user.permissions.includes(UserPermissions.RoleManageMembers)) {
    handleAddTeamMember = () => {
      const currentLocation = history.location;
      currentLocation.pathname = `/admin/roles/${roleId}/addMember`;
      history.push(currentLocation);
    };
  }

  const handleDeleteSelected = async () => {
    const data: RoleMembers = {
      members: [],
    };

    if (!selectedRows.current.length) return;

    if (role?.members.length !== selectedRows.current.length) {
      const filteredData = role?.members.filter(
        (member: any) =>
          !selectedRows.current.find(
            (selectedMember: any) => selectedMember.id === member.id
          )
      );
      filteredData?.forEach((member: any) => data.members.push(member.id));
    }

    try {
      await Api.roles.updateRoleMembers(roleId, data);
      window.location.reload();
    } catch (error) {
      console.error("Delete Role Members:", error);
    }
  };

  return (
    <Container>
      {!isLoading ? (
        <Container>
          <RoleForm
            header={RoleFormHeader.edit}
            submitValues={submitValues}
            value={formRole}
            canSave={user.permissions.includes(
              UserPermissions.RoleManageDetails
            )}
          />
          <Tabs
            rolePermissions={rolePermissions}
            setRolePermissions={setRolePermissions}
            members={role?.members}
            handleAddTeamMember={handleAddTeamMember}
            selectedRows={selectedRows}
            handleDeleteSelected={handleDeleteSelected}
            role={role?.name}
          />
        </Container>
      ) : null}
    </Container>
  );
};
